import { usePersistedState } from "@/lib/persistedFilters";
import { useCallback, useEffect, useMemo, useState, Fragment } from "react";
import { Search, RefreshCcw, Cloud, ChevronRight, Download, AlertTriangle, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import { downloadText } from "@/lib/download";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const RANGES = [
  { key: "1h", label: "1h" },
  { key: "6h", label: "6h" },
  { key: "24h", label: "24h" },
  { key: "7d", label: "7d" },
  { key: "30d", label: "30d" },
];

function NotConfigured({ status }) {
  return (
    <Card className="border-slate-200 p-5" data-testid="cloudtrail-not-configured">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-50 text-amber-600">
          <AlertTriangle className="h-4 w-4" />
        </span>
        <div className="space-y-3 text-sm text-slate-600">
          <p className="font-medium text-slate-800">AWS CloudTrail is not connected yet.</p>
          <p>
            Create an IAM user with one read-only permission —{" "}
            <code className="rounded bg-slate-100 px-1 py-0.5 text-xs">cloudtrail:LookupEvents</code> — then add its
            access key to the backend environment:
          </p>
          <pre className="overflow-x-auto rounded-lg bg-slate-900 p-3 text-xs leading-relaxed text-slate-100">
{`AWS_CLOUDTRAIL_ACCESS_KEY_ID=AKIA...
AWS_CLOUDTRAIL_SECRET_ACCESS_KEY=...
AWS_CLOUDTRAIL_REGION=${status?.region || "us-east-1"}
AWS_CLOUDTRAIL_REGIONS=us-east-1,ca-central-1`}
          </pre>
          <p className="text-xs text-slate-400">
            CloudTrail keeps the last {status?.max_lookback_days || 90} days of management events per region. Lookups are
            free and read-only — nothing is ever written back to AWS.
          </p>
        </div>
      </div>
    </Card>
  );
}

export function AwsCloudTrailPanel() {
  const [status, setStatus] = useState(null);
  const [region, setRegion] = useState("");
  const [range, setRange] = usePersistedState("cloudtrail.range", "24h");
  const [q, setQ] = usePersistedState("cloudtrail.q", "");
  const [writesOnly, setWritesOnly] = useState(false);
  const [rows, setRows] = useState([]);
  const [nextToken, setNextToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    api.get("/aws-cloudtrail/status")
      .then((r) => { setStatus(r.data); setRegion(r.data.region); })
      .catch(() => setStatus({ configured: false, regions: [], region: "us-east-1" }));
  }, []);

  const fetchPage = useCallback(async (token) => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/aws-cloudtrail/events", {
        params: { region, since: range, q: q.trim(), writes_only: writesOnly, limit: 50, next_token: token || "" },
      });
      setRows((prev) => (token ? [...prev, ...(data.items || [])] : data.items || []));
      setNextToken(data.next_token || "");
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load CloudTrail events");
      if (!token) setRows([]);
    } finally {
      setLoading(false);
    }
  }, [region, range, q, writesOnly]);

  // Debounced reload whenever a filter changes (CloudTrail allows 2 req/s).
  useEffect(() => {
    if (!status?.configured || !region) return;
    setExpanded(null);
    const t = setTimeout(() => fetchPage(""), 400);
    return () => clearTimeout(t);
  }, [status?.configured, region, range, q, writesOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  const counts = useMemo(() => ({
    total: rows.length,
    writes: rows.filter((r) => !r.read_only).length,
    errors: rows.filter((r) => r.error_code).length,
  }), [rows]);

  const exportCsv = () => {
    const header = ["Time", "User", "Event", "Service", "Region", "Source IP", "Result"];
    const body = rows.map((r) => [new Date(r.time).toLocaleString(), r.user, r.event_name, r.service,
      r.region, r.source_ip, r.error_code || "success"]);
    const esc = (s) => `"${String(s ?? "").replace(/"/g, '""')}"`;
    downloadText([header, ...body].map((x) => x.map(esc).join(",")).join("\n"), "aws-cloudtrail.csv");
    toast.success(`Exported ${rows.length} event${rows.length === 1 ? "" : "s"}`);
  };

  if (!status) return <Card className="p-6 text-sm text-slate-400">Loading…</Card>;
  if (!status.configured) return <NotConfigured status={status} />;

  return (
    <Card className="border-slate-200 shadow-sm" data-testid="cloudtrail-card">
      <div className="flex flex-col gap-3 border-b border-slate-100 p-4 lg:flex-row lg:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="cloudtrail-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search event, user, service, IP or resource…" className="pl-9" />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex overflow-hidden rounded-md border border-slate-200" data-testid="cloudtrail-ranges">
            {RANGES.map((r) => (
              <button key={r.key} type="button" data-testid={`cloudtrail-range-${r.key}`}
                onClick={() => setRange(r.key)}
                className={`px-2.5 py-1.5 text-xs font-medium transition-colors ${range === r.key ? "bg-primary text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`}>
                {r.label}
              </button>
            ))}
          </div>
          <Select value={region} onValueChange={setRegion}>
            <SelectTrigger className="h-9 w-[150px]" data-testid="cloudtrail-region"><SelectValue /></SelectTrigger>
            <SelectContent>
              {(status.regions || []).map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant={writesOnly ? "default" : "outline"} size="sm" data-testid="cloudtrail-writes-only"
            onClick={() => setWritesOnly((v) => !v)}>
            Writes only
          </Button>
          <Button variant="outline" size="sm" data-testid="cloudtrail-refresh" onClick={() => fetchPage("")} disabled={loading}>
            <RefreshCcw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
          <Button variant="outline" size="sm" data-testid="cloudtrail-export" onClick={exportCsv} disabled={!rows.length}>
            <Download className="h-4 w-4" /> CSV
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 border-b border-slate-100 bg-slate-50/60 px-4 py-2 text-xs text-slate-500">
        <span data-testid="cloudtrail-count"><strong className="text-slate-700">{counts.total}</strong> events</span>
        <span>· {counts.writes} write{counts.writes === 1 ? "" : "s"}</span>
        <span>· {counts.errors} failed</span>
        <span className="ml-auto flex items-center gap-1 text-slate-400">
          <Cloud className="h-3.5 w-3.5" /> {region} · last {range}
        </span>
      </div>

      {error && (
        <div className="border-b border-red-100 bg-red-50 px-4 py-2.5 text-sm text-red-700" data-testid="cloudtrail-error">
          {error}
        </div>
      )}

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Event</TableHead>
            <TableHead>User</TableHead>
            <TableHead>Service</TableHead>
            <TableHead>Region</TableHead>
            <TableHead>Source IP</TableHead>
            <TableHead>Result</TableHead>
            <TableHead>When</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading && !rows.length ? (
            <TableRow><TableCell colSpan={7} className="py-10 text-center text-sm text-slate-400">Loading CloudTrail events…</TableCell></TableRow>
          ) : !rows.length ? (
            <TableRow><TableCell colSpan={7} className="py-10 text-center text-sm text-slate-400" data-testid="cloudtrail-empty">
              No CloudTrail events match these filters.
            </TableCell></TableRow>
          ) : rows.map((r) => {
            const open = expanded === r.event_id;
            return (
              <Fragment key={r.event_id}>
                <TableRow data-testid={`cloudtrail-row-${r.event_id}`} className="cursor-pointer"
                  onClick={() => setExpanded(open ? null : r.event_id)}>
                  <TableCell className="py-2.5">
                    <div className="flex items-center gap-2">
                      <ChevronRight className={`h-3.5 w-3.5 shrink-0 text-slate-300 transition-transform ${open ? "rotate-90" : ""}`} />
                      <span className="text-sm font-medium text-slate-800">{r.event_name}</span>
                      {!r.read_only && <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700">write</span>}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-slate-600">
                    {r.user}
                    {r.user_type && <span className="ml-1 text-[10px] uppercase text-slate-400">{r.user_type}</span>}
                  </TableCell>
                  <TableCell className="text-sm text-slate-500">{r.service}</TableCell>
                  <TableCell className="whitespace-nowrap text-xs text-slate-400">{r.region || "—"}</TableCell>
                  <TableCell className="whitespace-nowrap text-xs text-slate-400">{r.source_ip || "—"}</TableCell>
                  <TableCell>
                    {r.error_code ? (
                      <span className="rounded bg-red-50 px-1.5 py-0.5 text-xs font-medium text-red-700" title={r.error_message}>{r.error_code}</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><ShieldCheck className="h-3.5 w-3.5" /> ok</span>
                    )}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-xs text-slate-400">{r.time ? new Date(r.time).toLocaleString() : "—"}</TableCell>
                </TableRow>
                {open && (
                  <TableRow className="hover:bg-transparent">
                    <TableCell colSpan={7} className="bg-slate-50/70 py-3" data-testid={`cloudtrail-raw-${r.event_id}`}>
                      {!!r.resources?.length && (
                        <div className="mb-2 flex flex-wrap gap-1.5">
                          {r.resources.map((res, i) => (
                            <span key={i} className="rounded bg-white px-2 py-0.5 text-xs text-slate-600 ring-1 ring-slate-200">{res}</span>
                          ))}
                        </div>
                      )}
                      <pre className="max-h-72 overflow-auto rounded-lg bg-slate-900 p-3 text-[11px] leading-relaxed text-slate-100">
                        {JSON.stringify(r.raw, null, 2)}
                      </pre>
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>

      {!!nextToken && (
        <div className="border-t border-slate-100 px-4 py-3 text-center">
          <Button variant="outline" size="sm" data-testid="cloudtrail-load-more" disabled={loading}
            onClick={() => fetchPage(nextToken)}>
            {loading ? "Loading…" : "Load more events"}
          </Button>
        </div>
      )}
    </Card>
  );
}

export default AwsCloudTrailPanel;
